// Screen · Home (role-aware) — 2026-09-30 redesign.
//
// A teacher's Home answers the one weekly question — "what do I teach, and where are the files?" —
// for EVERY class they teach, on one screen: the lesson title, one plain line, the Worksheet and
// Teacher Pack as big buttons, and the plan's status with the next action. The side column holds
// what is coming up at school and anything new from the Lead. (Before: Home had no lesson on it and
// repeated the menu; the files were two screens and a long scroll away.)
//
// The Lead's Home opens on the department: what waits for review, how many classes have not
// planned yet, and each class's status this week — then the Lead's own classes, because the Lead
// teaches too. Neutral by construction: no ranking, no red, no counters of anyone's "misses".

import { api } from "../api";
import { esc } from "../dom";
import { me } from "../state";
import { t } from "../i18n";
import { icon } from "../icons";
import { quoteOfTheDay } from "../quotes";
import { COACH_SET_ASIDE } from "../nav";
import { fmtDate, todayIso } from "../calendar-view";
import { materialsFor, resourcesFor } from "../materials";
import { resolveClassLesson, classWeekRows, nodeWhen, noStepLine, byGrade, plainText, termPhase, termWeekLabel, termNote, type ClassLesson, type ClassRow, type ClassWeekRow, type TermInfo } from "../lesson-data";
import { pageHeader, statusChip, bestState, fileButton, emptyState, dayLabel, rangeLabel, daysFromToday, type PlanState } from "../ui";
import type { WeekClock } from "../clock";
import { booksLineHtml, refsFor, teacherTitle } from "../lesson-refs";
import { worksheetButtonFor } from "../materials-engine";
import { mountWeeklyPreparation, weeklyQuery, type WeeklyContext } from "../weekly-preparation";

interface WeekLesson {
  id: string;
  topic: string;
  class_id: string;
  class_name: string;
  week_no: number;
  status: "not_started" | "in_progress" | "submitted" | "approved";
  plan_id: string | null;
  node_id: string | null;
  approval_note?: string | null;
}
interface UpcomingEntry { entry_date: string; end_date: string | null; label: string; type: string; grades: number[] | null }
interface ObsRow { id: string; kind: "classroom" | "marking"; observed_on: string; status: "draft" | "finalized" | "acknowledged" }
interface KeyEntry { entry_date: string; end_date: string | null; label: string }
interface CalChange {
  action: "create" | "edit" | "delete" | "restore";
  detail: string | null;
  created_at: string;
  actor_name: string;
  entry_label: string;
}

// One-time, dismissible "start here" card — accurate steps, never a tour or a modal. v2 (15 Oct 2026): the steps of the
// new planning workspace, so a teacher who dismissed the old card sees the new way once.
const HINT_KEY = "eden.hint.firstplan.v2";

/** Where a plan link should go: a draft opens its editor; a sent plan opens its page. */
function planHref(l: WeekLesson): string {
  if (l.status === "in_progress" || l.status === "not_started") return `/lesson/${encodeURIComponent(l.id)}/edit`;
  return l.plan_id ? `/plan/${encodeURIComponent(l.plan_id)}` : `/lesson/${encodeURIComponent(l.id)}`;
}

/** One class's card: this week's lesson, its files, its plan status and the next step. `classWeek`
 *  holds the lessons ON the class this week whoever wrote them: after the Lead moves a class, a plan its
 *  first teacher wrote still counts for the class, though only its writer (and the Lead) can open it. */
export async function lessonCardHtml(cl: ClassLesson, weekLessons: WeekLesson[], compact = false, classWeek: ClassWeekRow[] = [], weekly?: WeeklyContext): Promise<string> {
  const { cls, node, cur, grade } = cl;
  const mine = weekLessons.filter((l) => l.class_id === cls.id);
  const byOthers = classWeek.filter((l) => l.class_id === cls.id && !mine.some((m) => m.id === l.lesson_id));
  // The chip follows the viewer's own plans when there are any (their button does), else the class's.
  const state: PlanState = mine.length ? bestState(mine.map((l) => l.status)) : byOthers.length ? bestState(byOthers.map((l) => l.status)) : "none";
  const head = `<div class="lc-top">
      <span class="class-badge">${esc(cls.name)}</span>
      ${node ? `<span class="lc-when">${esc(nodeWhen(node))}</span>` : ""}
      <span class="lc-status">${statusChip(state)}</span>
    </div>`;

  if (!grade || !cur || !node) {
    const why = !grade ? t("lesson.no_grade") : t("lesson.no_map");
    return `<article class="lesson-card" data-class-id="${esc(cls.id)}">
      ${head}
      <p class="lc-summary muted">${why}</p>
      ${weekly ? `<section data-home-weekly="${esc(cls.id)}"></section>` : ""}
      <div class="lc-actions">
        <a class="btn" data-link href="/plan/start?class=${encodeURIComponent(cls.id)}">${icon("pencil")}<span>${t("week.plan_button")}</span></a>
      </div>
    </article>`;
  }

  const [mat, res, ref] = await Promise.all([materialsFor(node.id), resourcesFor(node.id), refsFor(node.id)]);
  // Between terms the lesson shown is the NEXT term's first week, so the running term's week is not its school context.
  const context = weekly && cl.week?.kind !== "holiday" ? { ...weekly, nodeId: node.id, lessonId: mine.length === 1 ? mine[0]!.id : null } : null;
  const mapHref = `/map/${encodeURIComponent(node.id)}?${context ? weeklyQuery(context) : new URLSearchParams({ class: cls.id })}`;
  // Teacher review, 15 Oct 2026 ("from not enough resources to many, they are a little overwhelmed"): the card keeps
  // ONE way to each thing — the lesson (where "Start with these" lists the book pages, the worksheet and the steps),
  // the plan (which opens beside the same materials) and the worksheet maker. The Worksheet / Teacher Pack file
  // buttons now live on the lesson page and in the plan's materials panel. A required file (a listening paper's
  // picture choices) stays here: the class cannot do the week without it.
  const files = (res?.items ?? []).filter((r) => r.required && r.href).map((r) => fileButton(r.href!, r.title, "pdf", ' data-file="required"')).join("");
  void mat;

  // The next action follows the plan's state: nothing yet → plan it; a draft → carry on; sent → view.
  // A plan the class's previous teacher wrote this week is named, not linked (it is theirs to open).
  const open = mine.length === 1 ? mine[0]! : null;
  const newHref = `/plan/start?${context ? weeklyQuery(context) : new URLSearchParams({ class: cls.id, node: node.id })}`;
  const othersNote = !mine.length && byOthers.length
    ? `<p class="lc-summary muted" data-planned-by-other>Already planned this week by the class's previous teacher — only they and the Lead can open that plan.</p>`
    : "";
  // A school week the grade's year plan has no step for: the nearest earlier lesson is shown, and the card says so.
  const noStep = cl.noStep ? `<p class="lc-summary muted" data-no-step>${esc(noStepLine(cl.week))}</p>` : "";
  const planAction = !mine.length
    ? byOthers.length
      ? `<a class="btn secondary" data-link data-plan-action="new" href="${esc(newHref)}">${icon("pencil")}<span>Plan your own</span></a>`
      : `<a class="btn" data-link data-plan-action="new" href="${esc(newHref)}">${icon("pencil")}<span>${t("lesson.plan")}</span></a>`
    : open
      ? `<a class="btn${state === "in_progress" || state === "not_started" ? "" : " secondary"}" data-link data-plan-action="open" href="${esc(planHref(open) + (context ? `?${weeklyQuery(context)}` : ""))}">${icon(state === "in_progress" || state === "not_started" ? "pencil" : "eye")}<span>${
          state === "in_progress" || state === "not_started" ? t("lesson.continue") : t("lesson.view_plan")
        }</span></a>`
      : `<a class="btn secondary" data-link data-plan-action="list" href="/plans?class=${encodeURIComponent(cls.id)}">${icon("plans")}<span>${mine.length} plans this week</span></a>`;

  return `<article class="lesson-card${compact ? " compact" : ""}" data-class-id="${esc(cls.id)}" data-node-id="${esc(node.id)}">
    ${head}
    <h3 class="lc-title"><a data-link href="${esc(mapHref)}">${esc(teacherTitle(node, ref))}</a></h3>
    ${mat?.oneliner && !compact ? `<p class="lc-summary">${esc(plainText(mat.oneliner))}</p>` : ""}
    ${noStep}
    ${booksLineHtml(ref)}
    ${files ? `<div class="lc-files">${files}</div>` : ""}
    ${othersNote}
    ${context ? `<section data-home-weekly="${esc(cls.id)}"></section>` : ""}
    <div class="lc-actions">
      ${planAction}
      <a class="btn secondary lc-open" data-link data-open-lesson href="${esc(mapHref)}">${icon("book")}<span>Open the lesson</span></a>
      ${await worksheetButtonFor(node.id, cls.id, "btn secondary", context ? { termId: context.termId, weekNo: context.weekNo, lessonId: context.lessonId ?? undefined } : undefined)}
    </div>
  </article>`;
}

/** The year's next key event (0019) as one line — "Next: Mid-term tests · 12 Oct · in 9 days". */
function nextKeyHtml(entries: KeyEntry[]): string {
  const today = todayIso();
  const next = entries.find((e) => (e.end_date ?? e.entry_date) >= today);
  if (!next) return "";
  const n = daysFromToday(next.entry_date);
  const vars = { label: next.label, date: fmtDate(next.entry_date), n };
  const line = n <= 0 ? t("home.next_key_today", vars) : n === 1 ? t("home.next_key_tomorrow", vars) : t("home.next_key_days", vars);
  return `<p class="hp-nextkey cu-next">${icon("flag")}<span>${esc(line)}</span></p>`;
}

/** "Changed this month" (P1b Job 3): the last 31 days of calendar changes, newest first, at most 3 —
 *  so everyone sees a moved date without being told. Quiet: nothing changed → nothing shown. */
function changedHtml(changes: CalChange[]): string {
  const cutoff = Date.now() - 31 * 86_400_000;
  const stamp = (iso: string): Date => new Date(iso.includes("T") ? iso : iso.replace(" ", "T") + "Z");
  const recent = changes.filter((c) => stamp(c.created_at).getTime() >= cutoff).slice(0, 3);
  if (!recent.length) return "";
  const short = (iso: string): string => dayLabel(iso).replace(/^\w+ /, ""); // "5 Oct"
  const lines = recent.map((c) => {
    let verb = "edited";
    if (c.action === "create") verb = "added";
    else if (c.action === "delete") verb = "removed";
    else if (c.action === "restore") verb = "restored";
    else if (c.detail) {
      try {
        const { from, to } = JSON.parse(c.detail) as { from?: Record<string, unknown>; to?: Record<string, unknown> };
        const f = (from?.entry_date ?? from?.end_date) as string | undefined;
        const tv = (to?.entry_date ?? to?.end_date) as string | undefined;
        if (f && tv) verb = `moved ${short(f)} → ${short(tv)}`;
      } catch { /* keep "edited" */ }
    }
    return `<p><b>${esc(c.entry_label)}</b> ${esc(verb)} <span class="muted">· ${esc(c.actor_name)}</span></p>`;
  }).join("");
  return `<div id="changed-this-month"><p class="cm-title">${t("home.changed_this_month")}</p>${lines}</div>`;
}

/** The "Coming up" panel: the next key event, the next few school dates (tests with their paper
 *  due date), and what changed on the calendar lately. */
function comingUpHtml(entries: UpcomingEntry[], clock: WeekClock | null, keys: KeyEntry[] = [], changes: CalChange[] = []): string {
  const rows = entries.slice(0, 5).map((e) => {
    const n = daysFromToday(e.entry_date);
    const when = n === 0 ? "Today" : n === 1 ? "Tomorrow" : dayLabel(e.entry_date);
    const due = clock?.next_test && clock.next_test.date === e.entry_date && e.type === "test"
      ? `<span class="cu-note">Test paper due ${esc(dayLabel(clock.next_test.paper_due))}</span>` : "";
    return `<li class="cu-row cu-${esc(e.type)}"><span class="cu-date">${esc(when)}</span><span class="cu-label">${esc(e.label)}${due}</span></li>`;
  }).join("");
  return `<section class="panel" aria-labelledby="cu-h">
    <h2 class="panel-title" id="cu-h">${icon("calendar")}Coming up</h2>
    ${nextKeyHtml(keys)}
    ${rows ? `<ul class="cu-list">${rows}</ul>` : `<p class="muted small">Nothing on the school calendar in the next weeks.</p>`}
    ${changedHtml(changes)}
    <a class="panel-link" data-link href="/calendar">Open the calendar ${icon("chevron", "ico-sm")}</a>
  </section>`;
}

function quoteHtml(): string {
  const q = quoteOfTheDay();
  return `<figure class="quote-card"><blockquote class="quote-text">${esc(q.en)}</blockquote>${
    q.ref && q.ref !== "Unknown" ? `<figcaption class="quote-ref">${esc(q.ref)}</figcaption>` : ""
  }</figure>`;
}

function weekLine(termInfo: TermInfo | null, clock: WeekClock | null): { eyebrow: string; sub: string } {
  if (!termInfo) return { eyebrow: "", sub: "" };
  if (termPhase(termInfo) !== "in") return { eyebrow: termWeekLabel(termInfo), sub: esc(termNote(termInfo)) };
  const due = clock ? `Plans for this week are due ${esc(dayLabel(clock.deadline))}.` : "";
  return { eyebrow: termWeekLabel(termInfo, clock ? rangeLabel(clock.week.start, clock.week.end) : ""), sub: due };
}

async function teacherHome(page: HTMLElement): Promise<void> {
  const user = me()!;
  const [termInfo, own, week, upcoming, clock, obs, keys, changes] = await Promise.all([
    api<TermInfo>("/api/terms/current").catch(() => null),
    api<{ classes: ClassRow[] }>("/api/classes?scope=own"),
    api<{ week_no: number | null; lessons: WeekLesson[] }>("/api/lessons?scope=week").catch(() => ({ week_no: null, lessons: [] })),
    api<{ entries: UpcomingEntry[] }>("/api/calendar?scope=upcoming").catch(() => ({ entries: [] })),
    api<WeekClock>("/api/clock").catch(() => null),
    api<{ observations: ObsRow[] }>("/api/observations").catch(() => ({ observations: [] })),
    api<{ entries: KeyEntry[] }>("/api/calendar?scope=key").catch(() => ({ entries: [] })),
    api<{ changes: CalChange[] }>("/api/calendar?scope=changes").catch(() => ({ changes: [] })),
  ]);
  const classes = [...own.classes].sort(byGrade);
  const classWeek = await classWeekRows(classes, week.week_no);
  const lessons = await Promise.all(classes.map((c) => resolveClassLesson(c, termInfo)));
  const cards = await Promise.all(lessons.map((cl) => lessonCardHtml(cl, week.lessons, false, classWeek, termInfo ? { classId: cl.cls.id, termId: termInfo.term.id, weekNo: termInfo.current_week ?? 1 } : undefined)));

  const { eyebrow, sub } = weekLine(termInfo, clock && "week" in clock ? clock : null);
  const hint = localStorage.getItem(HINT_KEY) || !classes.length
    ? ""
    : `<div class="start-card" id="first-hint">
        <div><p class="start-title">${icon("info")}Start here</p>
        <ol class="start-steps"><li><b>Open the lesson</b> — “Start with these” shows the book pages, the worksheet and the teaching steps.</li><li><b>Plan this lesson</b> — your plan opens beside the same materials, so you never have to go back and forth.</li><li>Send the plan to your Lead${clock && "week" in clock && daysFromToday(clock.deadline) >= 0 ? ` by ${esc(dayLabel(clock.deadline))}` : ""}.</li></ol>
        <p class="start-note muted small">Every “Plan” button in EDEN — on Home, the Year plan, a lesson or My plans — opens the same planning page.</p></div>
        <button type="button" class="linkbtn" id="hint-dismiss">Got it</button>
      </div>`;

  const main = classes.length
    ? cards.join("")
    : emptyState("You are not in a class yet. Your Lead adds classes in Setup — or you can add one yourself.",
        `<a class="btn secondary" data-link href="/classes">${icon("plus")}<span>Add a class</span></a>`);

  // Anything new from the Lead: this week's approval notes and records waiting for a signature.
  const notes = week.lessons.filter((l) => l.status === "approved" && l.approval_note);
  const toSign = obs.observations.filter((o) => o.status === "finalized");
  const leadItems = [
    ...toSign.map((o) => `<li><a data-link href="/observation/${esc(o.id)}">${icon("obs")}<span>A ${o.kind === "marking" ? "book & worksheet check" : "classroom observation"} is ready for you to read and sign.</span></a></li>`),
    ...notes.map((l) => `<li><a data-link href="${esc(planHref(l))}">${icon("check")}<span><b>${esc(l.class_name)}</b> plan approved: “${esc(l.approval_note!)}”</span></a></li>`),
  ].join("");
  const fromLead = `<section class="panel" aria-labelledby="fl-h">
    <h2 class="panel-title" id="fl-h">${icon("bell")}From your Lead</h2>
    ${leadItems ? `<ul class="fl-list">${leadItems}</ul>` : `<p class="muted small">Nothing new from your Lead.</p>`}
  </section>`;

  page.innerHTML = `
    ${pageHeader({ eyebrow, title: t("home.hello", { name: user.name }).replace(/\.$/, ""), sub })}
    ${hint}
    <div class="home-grid">
      <section class="home-main" aria-labelledby="tw-h">
        <h2 class="sec-title" id="tw-h">This week's lessons</h2>
        ${main}
      </section>
      <aside class="home-aside">
        ${fromLead}
        ${comingUpHtml(upcoming.entries, clock && "week" in clock ? clock : null, keys.entries, changes.changes)}
        <section class="panel quiet">
          <a class="panel-row" data-link href="/plans">${icon("plans")}<span>My plans</span>${icon("chevron", "ico-sm")}</a>
          <a class="panel-row" data-link href="/classes">${icon("classes")}<span>My classes &amp; student lists</span>${icon("chevron", "ico-sm")}</a>
          <a class="panel-row" data-link href="/dashboard">${icon("board")}<span>Team board</span>${icon("chevron", "ico-sm")}</a>
          ${COACH_SET_ASIDE ? "" : `<a class="panel-row" data-link href="/coach">${icon("obs")}<span>${t("home.coach")}</span>${icon("chevron", "ico-sm")}</a>`}
        </section>
        ${quoteHtml()}
      </aside>
    </div>`;
  wireHint(page);
  if (termInfo) await Promise.all(lessons.map(async (cl) => {
    const host = page.querySelector<HTMLElement>(`[data-home-weekly="${CSS.escape(cl.cls.id)}"]`);
    const mine = week.lessons.filter((l) => l.class_id === cl.cls.id);
    if (host) await mountWeeklyPreparation(host, { classId: cl.cls.id, termId: termInfo.term.id, weekNo: termInfo.current_week ?? 1, nodeId: cl.node?.id, lessonId: mine.length === 1 ? mine[0]!.id : null }, undefined, { link: true });
  }));
}

// ── the Lead's Home ───────────────────────────────────────────────────────────────────────────
interface StatusRow {
  teacher_name: string;
  class_name: string;
  week_no: number;
  status: WeekLesson["status"];
  on_time: boolean | null;
  submitted_at: string | null;
  plan_id: string | null;
}
interface TeamWeekLesson { teacher_name: string; class_name: string; status: WeekLesson["status"] }

/** This week's team rows for one class. The team week is Tier-1 (class name, writer, status: its key
 *  set is pinned by scripts/test-api.mjs) and carries no class id, so a class is found by its name,
 *  and by name + teacher only when two classes share that name. A class the Lead moved to another
 *  teacher keeps the plans its first teacher wrote this week. */
export function teamRowsFor<T extends { class_name: string; teacher_name: string }>(cls: ClassRow, teamWeek: T[], allClasses: ClassRow[]): T[] {
  const shared = allClasses.filter((c) => c.name === cls.name).length > 1;
  return teamWeek.filter((l) => l.class_name === cls.name && (!shared || l.teacher_name === (cls.teacher_name ?? "")));
}

/** A class's plan state this week, from the team week (see teamRowsFor). */
export function classStateThisWeek(cls: ClassRow, teamWeek: TeamWeekLesson[], allClasses: ClassRow[]): PlanState {
  const mine = teamRowsFor(cls, teamWeek, allClasses);
  return mine.length ? bestState(mine.map((l) => l.status)) : "none";
}

function whenAgo(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return "";
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  return days <= 0 ? "today" : days === 1 ? "yesterday" : `${days} days ago`;
}

export function reviewRowHtml(r: StatusRow): string {
  const late = r.on_time === false ? ` <span class="muted">(after the deadline)</span>` : "";
  return `<a class="row-link" data-link href="/plan/${esc(r.plan_id ?? "")}" data-review-row>
    <span class="rl-main"><b>${esc(r.teacher_name)}</b> · ${esc(r.class_name)} · Week ${esc(r.week_no)}</span>
    <span class="rl-meta">sent ${esc(whenAgo(r.submitted_at))}${late}</span>
    <span class="rl-go">Review ${icon("chevron", "ico-sm")}</span>
  </a>`;
}

async function leadHome(page: HTMLElement): Promise<void> {
  const user = me()!;
  const [termInfo, all, own, status, team, upcoming, clock, week, keys, changes] = await Promise.all([
    api<TermInfo>("/api/terms/current").catch(() => null),
    api<{ classes: ClassRow[] }>("/api/classes"),
    api<{ classes: ClassRow[] }>("/api/classes?scope=own"),
    api<{ rows: StatusRow[] }>("/api/lead/status"),
    api<{ lessons: TeamWeekLesson[]; week_no: number | null }>("/api/dashboard").catch(() => ({ lessons: [], week_no: null })),
    api<{ entries: UpcomingEntry[] }>("/api/calendar?scope=upcoming").catch(() => ({ entries: [] })),
    api<WeekClock>("/api/clock").catch(() => null),
    api<{ week_no: number | null; lessons: WeekLesson[] }>("/api/lessons?scope=week").catch(() => ({ week_no: null, lessons: [] })),
    api<{ entries: KeyEntry[] }>("/api/calendar?scope=key").catch(() => ({ entries: [] })),
    api<{ changes: CalChange[] }>("/api/calendar?scope=changes").catch(() => ({ changes: [] })),
  ]);

  const toReview = status.rows
    .filter((r) => r.status === "submitted" && r.plan_id)
    .sort((a, b) => (a.submitted_at ?? "").localeCompare(b.submitted_at ?? ""));
  const classes = [...all.classes].sort(byGrade);
  const states = classes.map((c) => ({ c, s: classStateThisWeek(c, team.lessons, classes) }));
  const notPlanned = states.filter((x) => x.s === "none").length;
  const approved = states.filter((x) => x.s === "approved").length;
  const clockOk = clock && "week" in clock ? clock : null;
  const next = clockOk?.next_test ?? null;

  // Before a term starts (or after it ends) the week these numbers describe is not "this week".
  const inTerm = !!termInfo && termPhase(termInfo) === "in";
  const wk = inTerm || !termInfo ? "this week" : `in Week ${termPhase(termInfo) === "before" ? 1 : termInfo.current_week ?? ""}`;
  const tile = (n: string, label: string, href: string, tone = ""): string =>
    `<a class="stat${tone ? " " + tone : ""}" data-link href="${href}"><span class="stat-n">${n}</span><span class="stat-l">${label}</span></a>`;
  const tiles = `<div class="stats">
    ${tile(String(toReview.length), toReview.length === 1 ? "plan to review" : "plans to review", "/team#review", toReview.length ? "stat-blue" : "")}
    ${tile(String(notPlanned), `${notPlanned === 1 ? "class" : "classes"} not planned yet ${wk}`, "/team#week")}
    ${tile(String(approved), `${approved === 1 ? "class" : "classes"} approved ${wk}`, "/team#week", approved ? "stat-green" : "")}
    ${next ? `<a class="stat" data-link href="/calendar"><span class="stat-n stat-date">${esc(dayLabel(next.date))}</span><span class="stat-l">${esc(next.label)} · papers due ${esc(dayLabel(next.paper_due))}</span></a>` : ""}
  </div>`;

  const review = toReview.length
    ? `<div class="list">${toReview.slice(0, 6).map(reviewRowHtml).join("")}</div>${
        toReview.length > 6 ? `<a class="panel-link" data-link href="/team#review">See all ${toReview.length} ${icon("chevron", "ico-sm")}</a>` : ""
      }`
    : emptyState("Nothing is waiting — every plan sent to you has been reviewed.");

  const table = classes.length
    ? `<div class="table-wrap"><table class="tbl tbl-inline">
        <thead><tr><th>Class</th><th>Teacher</th><th>${wk === "this week" ? "This week" : wk.replace(/^in /, "")}</th></tr></thead>
        <tbody>${states.map(({ c, s }) => `<tr>
          <td><a data-link href="/classes/${esc(c.id)}"><b>${esc(c.name)}</b></a>${c.level ? ` <span class="muted small">${esc(c.level)}</span>` : ""}</td>
          <td>${c.teacher_id ? `<a data-link href="/team/${esc(c.teacher_id)}">${esc(c.teacher_name ?? "")}</a>` : "—"}</td>
          <td>${statusChip(s, { lead: true })}</td></tr>`).join("")}</tbody>
      </table></div>`
    : emptyState("No classes yet. Add the department's classes in Setup.", `<a class="btn secondary" data-link href="/admin?tab=classes">${icon("setup")}<span>Set up classes</span></a>`);

  // The Lead teaches too: their own classes as compact lesson cards.
  const ownClasses = [...own.classes].sort(byGrade);
  const ownWeek = await classWeekRows(ownClasses, week.week_no);
  const ownLessons = await Promise.all(ownClasses.map((c) => resolveClassLesson(c, termInfo)));
  const ownCards = (await Promise.all(ownLessons.map((cl) => lessonCardHtml(cl, week.lessons, true, ownWeek, termInfo ? { classId: cl.cls.id, termId: termInfo.term.id, weekNo: termInfo.current_week ?? 1 } : undefined)))).join("");

  const { eyebrow, sub } = weekLine(termInfo, clockOk);
  page.innerHTML = `
    ${pageHeader({ eyebrow, title: t("home.hello", { name: user.name }).replace(/\.$/, ""), sub: sub || "Here is your department this week." })}
    ${tiles}
    <div class="home-grid">
      <section class="home-main">
        <h2 class="sec-title" id="review">Waiting for your review</h2>
        ${review}
        <h2 class="sec-title">${wk === "this week" ? "This week" : wk.replace(/^in /, "")}, class by class</h2>
        ${table}
        <a class="panel-link" data-link href="/team">Open Team ${icon("chevron", "ico-sm")}</a>
      </section>
      <aside class="home-aside">
        ${ownCards ? `<section class="panel"><h2 class="panel-title">${icon("book")}Your classes</h2>${ownCards}</section>` : ""}
        ${comingUpHtml(upcoming.entries, clockOk, keys.entries, changes.changes)}
        ${quoteHtml()}
      </aside>
    </div>`;
  if (termInfo) await Promise.all(ownLessons.map(async (cl) => {
    const host = page.querySelector<HTMLElement>(`[data-home-weekly="${CSS.escape(cl.cls.id)}"]`);
    const mine = week.lessons.filter((l) => l.class_id === cl.cls.id);
    if (host) await mountWeeklyPreparation(host, { classId: cl.cls.id, termId: termInfo.term.id, weekNo: termInfo.current_week ?? 1, nodeId: cl.node?.id, lessonId: mine.length === 1 ? mine[0]!.id : null }, undefined, { link: true });
  }));
}

function wireHint(page: HTMLElement): void {
  page.querySelector<HTMLElement>("#hint-dismiss")?.addEventListener("click", (e) => {
    e.preventDefault();
    try { localStorage.setItem(HINT_KEY, "1"); } catch { /* private mode — it just returns next visit */ }
    page.querySelector("#first-hint")?.remove();
  });
}

export async function renderHome(page: HTMLElement): Promise<void> {
  if (me()?.auth_role === "lead") await leadHome(page);
  else await teacherHome(page);
}
